import { useMemo, type ReactElement } from 'react';
import { Box } from '@mantine/core';
import { ListChecks } from 'lucide-react';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { TopicDefinition } from '@/shared/surfaces/TopicCatalogTypes';
import UseCaseBreakdownSection from './UseCaseBreakdownSection';

/** Backs the narration and LLM-insights panels — mirrors the retired `task-mix` catalog entry. */
const USE_CASES_DEFINITION: TopicDefinition = {
    id: 'use-cases',
    title: 'What work flows through',
    navLabel: 'Use Cases',
    hook: 'A handful of task types dominate the request stream.',
    question: 'Which task categories see the most activity?',
    icon: ListChecks,
    accent: 'teal',
    vizKind: 'ranked-bars',
    primaryField: 'task_category',
    headlineMetric: 'turns',
    unitLabel: 'prompts',
    lensLabel: 'task category',
    lensPlural: 'task categories',
};

/** The Use Cases landing: every task category ranked by activity (with its usage-category taxonomy parent), beside the summary and insights side rail. */
export default function UseCasesPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <UseCasesBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function UseCasesBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const ambientFilters = useAssistFilters();
    const filters = useMemo(() => [...ambientFilters], [ambientFilters]);

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent
                icon={ListChecks}
                eyebrow="Behavior"
                title="Use Cases"
                subtitle="The kinds of work people bring to AI — every task category, ranked by activity."
            />

            <UseCaseBreakdownSection
                field="task_category"
                filters={filters}
                withParents
                childField="task_subcategory"
                definition={USE_CASES_DEFINITION}
                surfaceTitle={surface.title}
                lensHeader="Category"
                hint="Expand a category to see its subcategories, or open one for its people and prompts →"
                hrefFor={(row) =>
                    row.parentValue === null ?
                        `${surface.basePath}/use-cases/${encodeURIComponent(row.value)}`
                    :   `${surface.basePath}/use-cases/${encodeURIComponent(row.parentValue)}/sub/${encodeURIComponent(row.value)}`
                }
            />
        </Box>
    );
}
